import { useInfiniteList } from "src/system/useInfiniteList.tsx";
import type {
  DefaultError,
  InfiniteData,
  UseInfiniteQueryOptions,
} from "@tanstack/react-query";
import type { ValidGetListResponse } from "src/system/SystemProvider.tsx";
import type {
  GridFilterModel,
  GridValidRowModel,
} from "@mui/x-data-grid-premium";
import type { GridSortModel } from "@mui/x-data-grid/models";
import type { SelectSearchField } from "src/components/Form/Form.types.ts";
import { getResourceBySelectValue } from "./getResourceBySelectValue";
import { getSelectFilter } from "./getSelectFilter";
import { getSelectSort } from "./getSelectSort";
import type { SelectLabelProp, SelectValueProp } from "./system.types";
import { useSelectOptions } from "./useSelectOptions";

export const useInfiniteSelect = <
  Resource extends GridValidRowModel = GridValidRowModel,
>({
  resource,
  queryOptions,
  filter: filterProp,
  sort: sortProp,
  pageSize,
  initialPage,
  locale,
  search,
  searchField,
  include,
  paginationMode = "simple",
  labelProp,
  valueProp,
}: {
  resource: string;
  labelProp?: SelectLabelProp<Resource>;
  valueProp?: SelectValueProp<Resource>;
  searchField?: SelectSearchField | { (search: string): SelectSearchField };
  include?: string[];
  locale?: string;
  paginationMode?: "simple";
  search?: string;
  filter?: GridFilterModel;
  sort?: GridSortModel;
  initialPage?: number;
  pageSize?: number;
  queryOptions?: Omit<
    UseInfiniteQueryOptions<
      InfiniteData<ValidGetListResponse<Resource>>,
      DefaultError,
      InfiniteData<ValidGetListResponse<Resource>>,
      any,
      any,
      number
    >,
    "queryKey" | "queryFn" | "getNextPageParam" | "initialPageParam"
  >;
}) => {
  const filter = getSelectFilter({
    labelProp,
    search,
    searchField,
    filter: filterProp,
  });

  const sort = getSelectSort({
    sort: sortProp,
    resource,
  });
  const {
    query,
    queryKey,
    getQueryKey: baseGetQueryKey,
  } = useInfiniteList({
    resource,
    pageSize,
    include,
    locale,
    paginationMode,
    filter,
    sort,
    initialPage,
    queryOptions: {
      refetchOnMount: true,
      staleTime: search ? 0 : Infinity,
      ...queryOptions,
    },
  });
  const { data } = query;
  const flatData = (data?.pages || []).flatMap((page) =>
    page.data.map((resource) => resource),
  );

  const options = useSelectOptions({
    valueProp,
    labelProp,
    resources: flatData,
  });

  const getResourceByValue = (value: any) => {
    return getResourceBySelectValue({
      value,
      resources: flatData,
      valueProp,
    });
  };

  const getQueryKey = ({ search }: { search?: string } = {}) => {
    return baseGetQueryKey({
      filter: getSelectFilter({
        labelProp,
        search,
        searchField,
        filter: filterProp,
      }),
    });
  };

  return {
    query,
    options,
    queryKey,
    getQueryKey,
    getResourceByValue,
    resources: flatData,
  };
};
